// OKDC POS 주문 API
const getApiUrl = () => {
  // 1순위: 환경변수
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2순위: 현재 도메인 기반 (woworder.store)
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    // 이미 같은 오리진 사용 (woworder.store)
    return origin;
  }

  // 3순위: 로컬 개발 환경
  return 'http://localhost:5000';
};

const API_URL = getApiUrl();

/**
 * OKDC 마스터 데이터 동기화
 * @param {Function} onProgress - 진행 상황 콜백 함수
 * @returns {Promise<Object>} 동기화 결과
 */
export const syncOKDCMasterData = async (onProgress) => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      throw new Error('인증 토큰이 없습니다.');
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    const storeId = payload.storeId || payload.id; // storeId 또는 id 사용

    if (onProgress) onProgress('OKDC 동기화 요청 중...');

    const response = await fetch(`${API_URL}/api/okdc/sync/master`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ storeId })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'OKDC 동기화 실패');
    }

    if (onProgress) onProgress('동기화 완료!');

    return {
      success: true,
      message: result.message || 'OKDC 마스터 데이터 동기화 완료'
    };
  } catch (error) {
    console.error('OKDC 동기화 오류:', error);
    return {
      success: false,
      message: error.message || 'OKDC 동기화 중 오류가 발생했습니다.'
    };
  }
};

// 테이블 세션에서 다음 SEQ 가져오기 (원자적 증가)
export const getNextOrderSeq = async (storeId, tableCode) => {
  console.log('[OKDC API] 🟠 getNextOrderSeq 호출됨, 매장:', storeId, '테이블:', tableCode);
  try {
    const response = await fetch(`${API_URL}/api/okdc/table-session/next-seq`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ storeId, tableCode })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || '세션 처리 실패');
    }

    return result;
  } catch (error) {
    console.error('세션 처리 오류:', error);
    throw error;
  }
};

// OKDC 주문 포맷 변환
export const formatOrderForOKDC = async (cartItems, tableCode, storeInfo) => {
  // 서버에서 ORDER_NO와 증가된 SEQ를 원자적으로 가져오기 (동시성 안전)
  const session = await getNextOrderSeq(storeInfo.storeId, tableCode);

  const orderNo = session.orderNo;
  const orderSeq = session.orderSeq; // 이미 패딩된 문자열 ("0001", "0002"...)
  const orderDate = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);

  console.log(`[OKDC 주문 포맷] 테이블 ${tableCode}, ${session.isNewSession ? '첫 주문' : '추가주문'}, 주문번호: ${orderNo}, SEQ: ${orderSeq}`);

  const orderInfo = [];

  cartItems.forEach((item, index) => {
    console.log(`[OKDC 주문] 아이템 ${index + 1}:`, item);
    // 기본 상품 가격 (옵션 제외)
    const basePrice = parseInt(item.menuItem?.basePrice || item.menuItem?.price || item.price || 0);

    // VAT 계산 (과세 상품만, 기본 가격 기준)
    const baseDcmAmt = basePrice * item.quantity;
    const baseVatAmt = item.is_taxable === 'Y' ? Math.floor(baseDcmAmt / 11) : 0;

    // 메인 메뉴 항목 (PROD_TYPE: '0')
    const mainOrderItem = {
      ORDER_TYPE: 'Y',
      PROD_CD: item.prod_cd || item.menu_code,
      PROD_AMT: basePrice.toString(),
      DCM_AMT: baseDcmAmt.toString(),
      PROD_CNT: item.quantity.toString(),
      PROD_TYPE: '0', // 메인 메뉴
      VAT_AMT: baseVatAmt.toString(),
      SVC_TIP_AMT: '0'
    };

    orderInfo.push(mainOrderItem);

    // 옵션을 별도 ORDER_INFO 항목으로 추가
    if (item.selectedOptions && item.selectedOptions.length > 0) {
      item.selectedOptions.forEach(option => {
        const optionPrice = parseInt(option.option_price || option.price || 0);
        const optionDcmAmt = optionPrice * item.quantity;
        const optionVatAmt = item.is_taxable === 'Y' ? Math.floor(optionDcmAmt / 11) : 0;

        // 옵션 타입 판별 (option_type 또는 groupId로 판단)
        const optionType = option.option_type || '';
        const prodType = optionType === 'side_dish' ? '1' : '2'; // 1=선택메뉴, 2=속성

        const optionOrderItem = {
          ORDER_TYPE: 'Y',
          PROD_AMT: optionPrice.toString(),
          DCM_AMT: optionDcmAmt.toString(),
          PROD_CNT: item.quantity.toString(),
          PROD_TYPE: prodType,
          VAT_AMT: optionVatAmt.toString(),
          SVC_TIP_AMT: '0'
        };

        // 속성(PROD_TYPE='2')인 경우: SDA_CLSCD, SDA_CD 사용
        if (prodType === '2') {
          optionOrderItem.SDA_CLSCD = option.sda_cls_cd || '';
          optionOrderItem.SDA_CD = option.sda_cd || option.option_id || '';
          // 속성은 PROD_CD를 보내지 않음 (POS가 부모 상품 가격을 중복 계산하는 문제 방지)
        }
        // 선택메뉴(PROD_TYPE='1')인 경우: SDS_GRPCD, SDS_CLSCD, SDS_CD 사용
        else if (prodType === '1') {
          optionOrderItem.SDS_GRPCD = option.sds_grp_cd || '';
          optionOrderItem.SDS_CLSCD = option.sds_cls_cd || '';
          optionOrderItem.SDS_CD = option.sds_cd || option.option_id || '';
          optionOrderItem.PROD_CD = option.sds_prod_cd || item.prod_cd || item.menu_code;  // 선택메뉴 상품 코드
        }

        orderInfo.push(optionOrderItem);
      });
    }
  });

  console.log('[OKDC 주문] 생성된 ORDER_INFO:', orderInfo);

  return {
    tableCode,
    orderDate,
    orderNo,
    orderSeq,
    gstCnt: '1',
    orderInfo
  };
};

// OKDC 주문 전송 (WebSocket 사용)

// reviewBenefit = { name } | null — POS 로는 전송하지 않고 PC 알림창의 리뷰 서비스 슬롯에만 표시한다.
export const sendOrderToOKDC = async (orderData, storeId, reviewBenefit = null) => {
  try {
    const response = await fetch(`${API_URL}/api/okdc/order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        storeId,
        orderData,
        reviewBenefit
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'OKDC 주문 전송 실패');
    }

    // OKDC 응답 코드 확인
    if (result.data && result.data.RESULT_CODE) {
      const resultCode = result.data.RESULT_CODE;

      if (resultCode === '0000') {
        return {
          success: true,
          message: '주문이 성공적으로 전송되었습니다.',
          orderNo: orderData.orderNo,
          data: result.data
        };
      } else {
        // OKDC 에러 코드 처리
        const errorMessages = {
          '8603': '이미 주문된 테이블입니다.',
          '8604': '존재하지 않는 상품입니다.',
          '8605': '재고가 부족합니다.',
        };

        throw new Error(errorMessages[resultCode] || `OKDC 오류: ${result.data.RESULT_MSG || resultCode}`);
      }
    }

    return {
      success: true,
      message: '주문이 전송되었습니다.',
      orderNo: orderData.orderNo,
      data: result.data
    };

  } catch (error) {
    console.error('OKDC 주문 전송 오류:', error);
    return {
      success: false,
      message: error.message || 'OKDC 주문 전송 중 오류가 발생했습니다.'
    };
  }
};

// 테이블 정리 요청
export const clearTableOKDC = async (storeId, tableCode) => {
  try {
    const response = await fetch(`${API_URL}/api/okdc/table/clear`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        storeId,
        tableCode
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || '테이블 정리 실패');
    }

    // 테이블 정리 성공 (서버에서 세션 자동 종료됨)
    console.log(`[OKDC 테이블 정리] 테이블 ${tableCode} 세션 종료됨`);

    return {
      success: true,
      message: '테이블이 정리되었습니다.',
      data: result.data
    };

  } catch (error) {
    console.error('테이블 정리 오류:', error);
    return {
      success: false,
      message: error.message || '테이블 정리 중 오류가 발생했습니다.'
    };
  }
};

// OKDC 주문내역 조회
export const getOKDCOrderInfo = async (storeId, tableCode = '001') => {
  try {
    const response = await fetch(`${API_URL}/api/okdc/order-info`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        storeId,
        tableCode
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'OKDC 주문내역 조회 실패');
    }

    return {
      success: true,
      orders: result.orders || [],
      totalOrders: result.totalOrders || 0
    };

  } catch (error) {
    console.error('OKDC 주문내역 조회 오류:', error);
    return {
      success: false,
      message: error.message || 'OKDC 주문내역 조회 중 오류가 발생했습니다.',
      orders: []
    };
  }
};

/**
 * OKDC POS 테이블에 주문이 있는지 확인
 * @param {number} storeId - 매장 ID
 * @param {string} tableCode - 테이블 코드 (예: '001')
 * @returns {Promise<{hasOrder: boolean}>}
 */
export const checkTableOrderExists = async (storeId, tableCode = '001') => {
  try {
    console.log(`[OKDC 주문 체크] 🔍 시작 - storeId: ${storeId}, tableCode: ${tableCode}`);

    const orderInfo = await getOKDCOrderInfo(storeId, tableCode);

    console.log(`[OKDC 주문 체크] 📦 응답:`, orderInfo);

    if (!orderInfo.success) {
      console.log(`[OKDC 주문 체크] ❌ 실패 - hasOrder: false`);
      return { hasOrder: false };
    }

    // orders 배열에 실제 아이템이 있는 주문이 있는지 확인 (items가 빈 배열이면 주문 없음)
    const hasOrder = orderInfo.orders && orderInfo.orders.some(order =>
      order.items && order.items.length > 0
    );

    console.log(`[OKDC 주문 체크] ✅ 테이블 ${tableCode} 주문 존재 여부: ${hasOrder}, 주문 개수: ${orderInfo.orders?.length || 0}`);

    return { hasOrder };
  } catch (error) {
    console.error('[OKDC 주문 체크] ❌ 오류:', error);
    return { hasOrder: false };
  }
};

/**
 * OKDC POS 테이블 주문 번호 조회
 * @param {number} storeId - 매장 ID
 * @param {string} tableCode - 테이블 코드
 * @returns {Promise<{success: boolean, orderNumber: number}>}
 */
export const fetchOKDCTableOrderNumber = async (storeId, tableCode = '001') => {
  try {
    const orderInfo = await getOKDCOrderInfo(storeId, tableCode);

    if (!orderInfo.success || !orderInfo.orders || orderInfo.orders.length === 0) {
      console.log(`[OKDC] 테이블 ${tableCode}에 주문 없음, 주문번호 1 반환`);
      return { success: true, orderNumber: 1 };
    }

    // 가장 최근 주문의 주문 번호 반환
    const latestOrder = orderInfo.orders[0];
    const orderNumber = latestOrder.ORDER_NO || latestOrder.orderNo || 1;

    console.log(`[OKDC] 테이블 ${tableCode} 주문번호:`, orderNumber);

    return { success: true, orderNumber };
  } catch (error) {
    console.error('[OKDC] 테이블 주문번호 조회 오류:', error);
    return { success: true, orderNumber: 1 };
  }
};

// 직원 호출 — reasonIds 전송. 백엔드가 한국어로 변환해 POS 클라이언트로 forward.
export const sendCallToOKDC = async (storeId, tableName, reasonIds) => {
  try {
    const response = await fetch(`${API_URL}/api/okdc/staff-call`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableName, reasonIds })
    });
    const result = await response.json();
    if (!response.ok) {
      return { success: false, message: result.message || '호출 실패' };
    }
    return { success: true, callItems: result.callItems };
  } catch (error) {
    console.error('[OKDC] 직원 호출 오류:', error);
    return { success: false, message: error.message };
  }
};
